export type TeamId = 'red' | 'blue';
export type MatchPhase = 'warmup' | 'live' | 'overtime' | 'postgame';
export type ArmorClass = 'light' | 'medium' | 'heavy';
// Phase 171-176: full T2 arsenal — laser rifle (sniper), plasma gun, grenade launcher,
// ELF projector (energy drain), shocklance (close burst), missile launcher (vehicle killer).
export type WeaponId =
  | 'spinfusor'
  | 'chaingun'
  | 'mortar'
  | 'blaster'
  | 'laser-rifle'
  | 'plasma'
  | 'grenade-launcher'
  | 'elf'
  | 'shocklance'
  | 'missile-launcher'
  | 'frag'
  | 'concussion'
  | 'vehicle-blaster'
  | 'vehicle-missile';
export type GrenadeId = 'frag' | 'concussion';
// Phase 121: Pack roster extended for T2 combat depth — adds 'ammo' (passive mag boost)
// and 'cloak' (active invisibility) alongside existing classic packs.
export type PackId = 'energy' | 'repair' | 'shield' | 'sensor' | 'jammer' | 'ammo' | 'cloak';
// Phase 178: Shrike light air fighter — T2's signature flyer.
export type VehicleId = 'grav-bike' | 'assault-tank' | 'shrike';
// Phase 127/128: Add 'mine' and 'satchel' deployables to the canonical roster.
// Phase 179: turret-aa — anti-air/anti-vehicle turret to counter Shrikes and gunships.
export type DeployableId = 'sensor-beacon' | 'shield-node' | 'turret-small' | 'turret-aa' | 'ammo-supply' | 'mine' | 'satchel';
export type TeamCommandId =
  | 'attack'
  | 'defend'
  | 'regroup'
  | 'escort'
  | 'repair'
  | 'deploy'
  | 'vehicle'
  | 'generator'
  | 'sensor'
  | 'flag-request';
export type ObjectiveHintKind = 'capture' | 'return' | 'defense' | 'carrierStop' | 'route' | 'base';
export type BaseFlagHookKind =
  | 'generatorFlagLink'
  | 'inventoryFlagSupport'
  | 'repairFlagSupport'
  | 'turretFlagCoverage'
  | 'carrierPressure';
export type WeaponIdentityRole = 'burst' | 'sustained' | 'siege' | 'sidearm' | 'grenade' | 'utility' | 'snipe' | 'melee';

export type WeaponIdentity = {
  id: WeaponId;
  label: string;
  slotLabel: string;
  role: WeaponIdentityRole;
  roleLabel: string;
  tone: string;
  accent: string;
  glow: string;
  family: 'disc' | 'slug' | 'bolt';
  detail: string;
  readinessHint: string;
  cooldownWindow: number;
  trailScale: number;
};

export type ArmorProfile = {
  maxHealth: number;
  maxEnergy: number;
  groundAccelerationMultiplier: number;
  airAccelerationMultiplier: number;
  jetImpulseMultiplier: number;
  jetSustainMultiplier: number;
  jetEmergencyLiftMultiplier: number;
  jetDrainPerSecond: number;
  jetRecoveryPerSecond: number;
  scale: number;
};

export const TEAMS: TeamId[] = ['red', 'blue'];
export const ARMOR_CLASSES: ArmorClass[] = ['light', 'medium', 'heavy'];
export const WEAPON_IDS: WeaponId[] = [
  'spinfusor',
  'chaingun',
  'mortar',
  'blaster',
  'laser-rifle',
  'plasma',
  'grenade-launcher',
  'elf',
  'shocklance',
  'missile-launcher',
];
export const GRENADE_IDS: GrenadeId[] = ['frag', 'concussion'];
export const PACK_IDS: PackId[] = ['energy', 'repair', 'shield', 'sensor', 'jammer', 'ammo', 'cloak'];
export const VEHICLE_IDS: VehicleId[] = ['grav-bike', 'assault-tank', 'shrike'];
export const DEPLOYABLE_IDS: DeployableId[] = ['sensor-beacon', 'shield-node', 'turret-small', 'turret-aa', 'ammo-supply', 'mine', 'satchel'];
export const TEAM_COMMAND_IDS: TeamCommandId[] = [
  'attack',
  'defend',
  'regroup',
  'escort',
  'repair',
  'deploy',
  'vehicle',
  'generator',
  'sensor',
  'flag-request',
];

export const WEAPON_IDENTITIES: Record<WeaponId, WeaponIdentity> = {
  spinfusor: {
    id: 'spinfusor',
    label: 'Spinfusor',
    slotLabel: 'Slot 1',
    role: 'burst',
    roleLabel: 'Burst',
    tone: '#62ddff',
    accent: '#4488ff',
    glow: '#aaccff',
    family: 'disc',
    detail: 'Mid-range disc launcher for flag-route punishes and splash resets.',
    readinessHint: 'Splash entry / chase punish',
    cooldownWindow: 1,
    trailScale: 1.2,
  },
  chaingun: {
    id: 'chaingun',
    label: 'Chaingun',
    slotLabel: 'Slot 2',
    role: 'sustained',
    roleLabel: 'Sustained',
    tone: '#ffcc66',
    accent: '#ffcc66',
    glow: '#fff0bf',
    family: 'slug',
    detail: 'Tracking pressure weapon for medium-range cleanup and chase damage.',
    readinessHint: 'Tracking pressure / cleanup',
    cooldownWindow: 0.5,
    trailScale: 0.8,
  },
  mortar: {
    id: 'mortar',
    label: 'Mortar',
    slotLabel: 'Slot 3',
    role: 'siege',
    roleLabel: 'Siege',
    tone: '#ff9866',
    accent: '#ff4444',
    glow: '#ffaaaa',
    family: 'slug',
    detail: 'Heavy arc shell for generator rooms, flag stands, and base denial.',
    readinessHint: 'Base shelling / room denial',
    cooldownWindow: 1.6,
    trailScale: 1.4,
  },
  blaster: {
    id: 'blaster',
    label: 'Blaster',
    slotLabel: 'Slot 4',
    role: 'sidearm',
    roleLabel: 'Sidearm',
    tone: '#a8c6ff',
    accent: '#e8e8ff',
    glow: '#ffffff',
    family: 'bolt',
    detail: 'Fast close-range finisher for tight interiors and emergency duels.',
    readinessHint: 'Interior duels / fast follow-up',
    cooldownWindow: 0.4,
    trailScale: 0.7,
  },
  'laser-rifle': {
    id: 'laser-rifle',
    label: 'Laser Rifle',
    slotLabel: 'Slot 5',
    role: 'snipe',
    roleLabel: 'Sniper',
    tone: '#7dff9c',
    accent: '#37d66a',
    glow: '#d2ffdd',
    family: 'bolt',
    detail: 'Charged long-range beam for pick damage on carriers, snipers, and heavies.',
    readinessHint: 'Long-range picks / carrier stops',
    cooldownWindow: 1,
    trailScale: 0.6,
  },
  plasma: {
    id: 'plasma',
    label: 'Plasma Gun',
    slotLabel: 'Slot 6',
    role: 'sustained',
    roleLabel: 'Sustained',
    tone: '#ff7de6',
    accent: '#ff37c2',
    glow: '#ffd2f4',
    family: 'disc',
    detail: 'Arcing plasma bolts that shred medium and heavy armor at close-medium range.',
    readinessHint: 'Armor melt / interior pressure',
    cooldownWindow: 0.6,
    trailScale: 1.0,
  },
  'grenade-launcher': {
    id: 'grenade-launcher',
    label: 'Grenade Launcher',
    slotLabel: 'Slot 7',
    role: 'siege',
    roleLabel: 'Bounce',
    tone: '#ffd166',
    accent: '#ffb020',
    glow: '#fff0c2',
    family: 'disc',
    detail: 'Bouncing explosive spammer for corners, chokes, and indirect area denial.',
    readinessHint: 'Corner denial / indirect pressure',
    cooldownWindow: 0.9,
    trailScale: 1.15,
  },
  elf: {
    id: 'elf',
    label: 'ELF Projector',
    slotLabel: 'Slot 8',
    role: 'utility',
    roleLabel: 'Drain',
    tone: '#66f0ff',
    accent: '#20d8ff',
    glow: '#c2fbff',
    family: 'bolt',
    detail: 'Continuous energy-drain beam that grounds jetting light armors and starves shields.',
    readinessHint: 'Energy denial / light shutdown',
    cooldownWindow: 0.3,
    trailScale: 0.5,
  },
  shocklance: {
    id: 'shocklance',
    label: 'Shocklance',
    slotLabel: 'Slot 9',
    role: 'melee',
    roleLabel: 'Melee',
    tone: '#b58cff',
    accent: '#8c5cff',
    glow: '#e6dcff',
    family: 'bolt',
    detail: 'Close-range instant-hit lance with a devastating rear-hit bonus for cappers.',
    readinessHint: 'Point-blank burst / rear kills',
    cooldownWindow: 1.2,
    trailScale: 0.7,
  },
  'missile-launcher': {
    id: 'missile-launcher',
    label: 'Missile Launcher',
    slotLabel: 'Slot 0',
    role: 'burst',
    roleLabel: 'Heavy',
    tone: '#ff8d74',
    accent: '#ff4444',
    glow: '#ffc7bb',
    family: 'slug',
    detail: 'Heavy dumb-fire missile for vehicle busting, base assets, and heavy-armor punishes.',
    readinessHint: 'Vehicle busting / heavy punish',
    cooldownWindow: 2.0,
    trailScale: 1.5,
  },
  frag: {
    id: 'frag',
    label: 'Frag',
    slotLabel: 'Grenade',
    role: 'grenade',
    roleLabel: 'Explosive',
    tone: '#ff9866',
    accent: '#ff9b71',
    glow: '#ffd6c7',
    family: 'disc',
    detail: 'Thrown explosive for stand clears, route denial, and soft target finishes.',
    readinessHint: 'Stand clear / route denial',
    cooldownWindow: 1.15,
    trailScale: 1.1,
  },
  concussion: {
    id: 'concussion',
    label: 'Concussion',
    slotLabel: 'Grenade',
    role: 'grenade',
    roleLabel: 'Disrupt',
    tone: '#b58cff',
    accent: '#b58cff',
    glow: '#efe3ff',
    family: 'bolt',
    detail: 'Impulse grenade for carrier disruption, air denial, and forced route errors.',
    readinessHint: 'Carrier disruption / forced movement',
    cooldownWindow: 1.15,
    trailScale: 1.3,
  },
  'vehicle-blaster': {
    id: 'vehicle-blaster',
    label: 'Blaster',
    slotLabel: 'Vehicle',
    role: 'sustained',
    roleLabel: 'Mounted',
    tone: '#62ddff',
    accent: '#4488ff',
    glow: '#aaccff',
    family: 'bolt',
    detail: 'Rapid-fire mounted blaster for grav-bike.',
    readinessHint: 'Vehicle blaster',
    cooldownWindow: 0.4,
    trailScale: 0.8,
  },
  'vehicle-missile': {
    id: 'vehicle-missile',
    label: 'Missile',
    slotLabel: 'Vehicle',
    role: 'burst',
    roleLabel: 'Heavy',
    tone: '#ff8d74',
    accent: '#ff4444',
    glow: '#ffaaaa',
    family: 'slug',
    detail: 'Heavy missile launcher for assault tank.',
    readinessHint: 'Vehicle missile',
    cooldownWindow: 2.5,
    trailScale: 1.4,
  },
};

// Phase 171-177: authentic T2 class weapon access.
// Light = mobile pick class (sniper laser, ELF, shocklance). Heavy = siege bruiser
// (mortar, grenade launcher, missile launcher). Medium bridges both.
const ARMOR_WEAPON_RULES: Record<ArmorClass, WeaponId[]> = {
  light: ['spinfusor', 'chaingun', 'laser-rifle', 'plasma', 'elf', 'shocklance', 'blaster'],
  medium: ['spinfusor', 'chaingun', 'laser-rifle', 'plasma', 'grenade-launcher', 'elf', 'shocklance', 'blaster'],
  heavy: ['spinfusor', 'chaingun', 'mortar', 'plasma', 'grenade-launcher', 'missile-launcher', 'shocklance', 'blaster'],
};

// Phase 121: extend armor pack rosters with the new 'ammo' (passive mag boost) and
// 'cloak' (active invisibility) packs. Cloak is light/medium only; ammo is universal.
const ARMOR_PACK_RULES: Record<ArmorClass, PackId[]> = {
  light: ['energy', 'repair', 'sensor', 'ammo', 'cloak'],
  medium: ['energy', 'repair', 'shield', 'sensor', 'jammer', 'ammo', 'cloak'],
  heavy: ['energy', 'shield', 'jammer', 'ammo'],
};

const ARMOR_PROFILES: Record<ArmorClass, ArmorProfile> = {
  light: {
    maxHealth: 85,
    maxEnergy: 125,
    groundAccelerationMultiplier: 1.14,
    airAccelerationMultiplier: 1.18,
    jetImpulseMultiplier: 1.16,
    jetSustainMultiplier: 1.8,
    jetEmergencyLiftMultiplier: 0.88,
    jetDrainPerSecond: 32,
    jetRecoveryPerSecond: 27,
    scale: 0.9,
  },
  medium: {
    maxHealth: 100,
    maxEnergy: 100,
    groundAccelerationMultiplier: 1,
    airAccelerationMultiplier: 1,
    jetImpulseMultiplier: 1,
    jetSustainMultiplier: 1,
    jetEmergencyLiftMultiplier: 1,
    jetDrainPerSecond: 28,
    jetRecoveryPerSecond: 22,
    scale: 1,
  },
  heavy: {
    maxHealth: 150,
    maxEnergy: 85,
    groundAccelerationMultiplier: 0.88,
    airAccelerationMultiplier: 0.82,
    jetImpulseMultiplier: 0.84,
    jetSustainMultiplier: 0.45,
    jetEmergencyLiftMultiplier: 5.0,
    jetDrainPerSecond: 18,
    jetRecoveryPerSecond: 22,
    // Phase 69 — bulkier T2 Heavy silhouette
    scale: 1.2,
  },
};

export function otherTeam(team: TeamId): TeamId {
  return team === 'red' ? 'blue' : 'red';
}

export function pickBalancedTeam(counts: Record<TeamId, number>): TeamId {
  if (counts.red === counts.blue) {
    return 'red';
  }

  return counts.red < counts.blue ? 'red' : 'blue';
}

export function getArmorProfile(armorClass: ArmorClass | null | undefined): ArmorProfile {
  if (armorClass === 'light' || armorClass === 'heavy') {
    return { ...ARMOR_PROFILES[armorClass] };
  }

  return { ...ARMOR_PROFILES.medium };
}

export function getArmorMovementTuning(armorClass: ArmorClass | null | undefined) {
  const profile = getArmorProfile(armorClass);

  return {
    maxEnergy: profile.maxEnergy,
    groundAccelerationMultiplier: profile.groundAccelerationMultiplier,
    airAccelerationMultiplier: profile.airAccelerationMultiplier,
    jetImpulseMultiplier: profile.jetImpulseMultiplier,
    jetSustainMultiplier: profile.jetSustainMultiplier,
    jetEmergencyLiftMultiplier: profile.jetEmergencyLiftMultiplier,
    jetDrainPerSecond: profile.jetDrainPerSecond,
    jetRecoveryPerSecond: profile.jetRecoveryPerSecond,
  };
}

export function getWeaponIdentity(weaponId: WeaponId | string | null | undefined): WeaponIdentity | null {
  const key = `${weaponId ?? ''}`.toLowerCase() as WeaponId;
  return WEAPON_IDENTITIES[key] ? { ...WEAPON_IDENTITIES[key] } : null;
}

export function defaultArmorClass(isBot = false, botIndex = 0): ArmorClass {
  if (!isBot) {
    return 'medium';
  }

  return botIndex % 2 === 0 ? 'light' : 'heavy';
}

export function legalWeaponIdsForArmor(armorClass: ArmorClass | null | undefined): WeaponId[] {
  if (armorClass === 'light' || armorClass === 'heavy') {
    return [...ARMOR_WEAPON_RULES[armorClass]];
  }

  return [...ARMOR_WEAPON_RULES.medium];
}

export function isWeaponAllowedForArmor(
  armorClass: ArmorClass | null | undefined,
  weaponId: WeaponId | null | undefined,
): boolean {
  if (!weaponId) {
    return false;
  }

  return legalWeaponIdsForArmor(armorClass).includes(weaponId);
}

export function legalPackIdsForArmor(armorClass: ArmorClass | null | undefined): PackId[] {
  if (armorClass === 'light' || armorClass === 'heavy') {
    return [...ARMOR_PACK_RULES[armorClass]];
  }

  return [...ARMOR_PACK_RULES.medium];
}

export function isPackAllowedForArmor(
  armorClass: ArmorClass | null | undefined,
  packId: PackId | null | undefined,
): boolean {
  if (!packId) {
    return false;
  }

  return legalPackIdsForArmor(armorClass).includes(packId);
}

export function resolveLegalLoadout(
  armorClass: ArmorClass | null | undefined,
  weaponId: WeaponId | null | undefined,
  packId: PackId | null | undefined,
) {
  const legalWeapons = legalWeaponIdsForArmor(armorClass);
  const legalPacks = legalPackIdsForArmor(armorClass);

  return {
    weaponId: weaponId && legalWeapons.includes(weaponId) ? weaponId : (legalWeapons[0] ?? WEAPON_IDS[0]),
    packId: packId && legalPacks.includes(packId) ? packId : (legalPacks[0] ?? PACK_IDS[0]),
  };
}
